import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../api/axios';
import { CUTOFF_START_YEAR, CUTOFF_END_YEAR } from '../constants/years';
import { FaSpinner, FaArrowUp, FaArrowDown, FaMinus, FaMapMarkerAlt, FaBuilding, FaGraduationCap, FaPlus, FaCheck, FaTimes, FaSearch, FaFilter, FaStar } from 'react-icons/fa';

interface DepartmentDemand {
  branch_name: string;
  branch_code: string;
  avg_closing_rank: number;
  total_seats: number;
  seats_filled: number;
  fill_rate: number;
  trend: 'high_demand' | 'medium_demand' | 'low_demand';
  rank_change_5yr: number;
}

interface College {
  id: number;
  college_name: string;
  college_code: string;
  college_type: string;
  autonomous_status: boolean;
  naac_grade: string | null;
  avg_rank_across_depts: number;
  total_departments: number;
  departments: DepartmentDemand[];
}

interface DistrictRanking {
  district_name: string;
  district_id: number;
  total_colleges: number;
  total_departments: number;
  colleges: College[];
  isLoaded: boolean;
  isLoading: boolean;
}

interface DistrictOption {
  id: number;
  name: string;
}

interface BranchOption {
  branch_code: string;
  branch_name: string;
  display_name: string;
}

const DistrictRankings = () => {
  const [rankings, setRankings] = useState<DistrictRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCommunity, setSelectedCommunity] = useState<string>('OC');
  const [selectedYear, setSelectedYear] = useState<number>(CUTOFF_END_YEAR);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedCollegeType, setSelectedCollegeType] = useState<string>('');
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [expandedDistricts, setExpandedDistricts] = useState<Set<number>>(new Set());
  const [expandedColleges, setExpandedColleges] = useState<Set<number>>(new Set());
  const [loadedDistricts, setLoadedDistricts] = useState<Set<number>>(new Set());
  const [rankingMethod, setRankingMethod] = useState<'colleges' | 'quality' | 'bestvalue'>('colleges');
  const [bestValueColleges, setBestValueColleges] = useState<any[]>([]);
  const [bestValueLoading, setBestValueLoading] = useState(false);
  const [bestValueDistrict, setBestValueDistrict] = useState('');

  // Dropdown options
  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [branches, setBranches] = useState<BranchOption[]>([]);

  // Cache for API responses to avoid re-fetching
  const [districtDataCache, setDistrictDataCache] = useState<Map<string, any>>(new Map());

  // College selection for comparison
  const [selectedColleges, setSelectedColleges] = useState<Map<number, any>>(new Map());
  const navigate = useNavigate();

  const communities = ['OC', 'BC', 'BCM', 'MBC', 'SC', 'SCA', 'ST'];
  const years = Array.from({ length: CUTOFF_END_YEAR - CUTOFF_START_YEAR + 1 }, (_, i) => CUTOFF_END_YEAR - i);
  const collegeTypes = ['Government', 'Anna University', 'Aided', 'Private'];

  // Fetch dropdown options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [districtRes, branchRes] = await Promise.all([
          axios.get('/districts'),
          axios.get('/courses/branches/distinct'),
        ]);
        setDistricts(districtRes.data?.districts || districtRes.districts || []);
        setBranches(branchRes.branches || branchRes.data?.branches || []);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchOptions();
  }, []);

  // Summary stats
  const summaryText = useMemo(() => {
    if (rankings.length === 0) return '';
    if (rankingMethod === 'colleges') {
      const districtCount = new Set(rankings.map(r => r.district_name)).size;
      return `Showing ${rankings.length} colleges across ${districtCount} districts`;
    }
    const totalColleges = rankings.reduce((sum, d) => sum + d.total_colleges, 0);
    return `${rankings.length} districts with ${totalColleges} colleges`;
  }, [rankings, rankingMethod]);

  const activeFilters = useMemo(() => {
    const filters: string[] = [];
    filters.push(selectedCommunity);
    filters.push(String(selectedYear));
    if (selectedDistrict) filters.push(selectedDistrict);
    if (selectedCollegeType) filters.push(selectedCollegeType);
    if (selectedBranch) {
      const branch = branches.find(b => b.branch_code === selectedBranch);
      if (branch) filters.push(branch.display_name);
    }
    return filters;
  }, [selectedCommunity, selectedYear, selectedDistrict, selectedCollegeType, selectedBranch, branches]);

  useEffect(() => {
    if (rankingMethod === 'bestvalue') {
      fetchBestValue();
    } else {
      fetchDistrictSummary();
      setDistrictDataCache(new Map());
    }
  }, [selectedCommunity, selectedYear, rankingMethod, selectedDistrict, selectedCollegeType, selectedBranch, bestValueDistrict]);

  const NAAC_SCORE: Record<string, number> = { 'A++': 5, 'A+': 4, 'A': 3, 'B++': 2, 'B+': 1, 'B': 0.5 };
  const TYPE_SCORE: Record<string, number> = { 'Government': 1, 'Anna University': 0.95, 'Aided': 0.85, 'Govt Aided': 0.85, 'Anna Univ - Constituent': 0.9, 'Private': 0.5 };
  const MAX_FEE = 150000;

  const computeBestValueScore = (c: any) => {
    const naac = (NAAC_SCORE[c.naac_grade] ?? 0) / 5;
    const type = TYPE_SCORE[c.college_type] ?? 0.5;
    const fee = c.annual_tuition_fee ? Math.max(0, 1 - c.annual_tuition_fee / MAX_FEE) : 0.5;
    return (naac * 0.4 + fee * 0.4 + type * 0.2) * 100;
  };

  const fetchBestValue = async () => {
    setBestValueLoading(true);
    try {
      const params: any = { limit: 300 };
      if (bestValueDistrict) params.district_id = bestValueDistrict;
      const res: any = await axios.get('/colleges', { params });
      // After interceptor: res = { success, data: { colleges: [...] } }
      const cols = res.data?.colleges || [];
      const scored = cols
        .map((c: any) => ({ ...c, bestValueScore: computeBestValueScore(c) }))
        .sort((a: any, b: any) => b.bestValueScore - a.bestValueScore)
        .slice(0, 50);
      setBestValueColleges(scored);
    } catch {} finally {
      setBestValueLoading(false);
    }
  };

  const fetchDistrictSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadedDistricts(new Set());
      setExpandedDistricts(new Set());
      setExpandedColleges(new Set());

      const params: any = {
        year: selectedYear,
        community: selectedCommunity,
        ranking_method: rankingMethod,
      };
      if (selectedDistrict) params.district = selectedDistrict;
      if (selectedCollegeType) params.college_type = selectedCollegeType;
      if (selectedBranch) params.branch_code = selectedBranch;

      const summaryResponse = await axios.get('/cutoffs/district-summary', { params });
      const rawData = summaryResponse.data.districts || [];

      if (rankingMethod === 'colleges') {
        const districtRankings: DistrictRanking[] = rawData.map((college: any) => ({
          district_name: college.district_name || 'Unknown',
          district_id: college.district_id || 0,
          total_colleges: 1,
          total_departments: college.total_departments || 0,
          colleges: [{
            id: college.college_id || 0,
            college_name: college.college_name || 'Unknown College',
            college_code: college.college_code || 'N/A',
            college_type: college.college_type || 'Unknown',
            autonomous_status: false,
            naac_grade: null,
            avg_rank_across_depts: college.avg_closing_rank ? Math.round(college.avg_closing_rank) : 0,
            total_departments: college.total_departments || 0,
            departments: [],
          }],
          isLoaded: true,
          isLoading: false,
        }));
        setRankings(districtRankings);
      } else {
        const districtRankings: DistrictRanking[] = rawData.map((district: any) => ({
          district_name: district.district_name,
          district_id: district.district_id,
          total_colleges: district.total_colleges,
          total_departments: district.total_departments,
          colleges: [],
          isLoaded: false,
          isLoading: false,
        }));
        setRankings(districtRankings);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch district rankings');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Competitiveness label helper
  const getCompetitivenessLabel = (rank: number) => {
    if (rank < 5000) return { text: 'Very Competitive', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-200' };
    if (rank < 15000) return { text: 'Competitive', color: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-200' };
    if (rank < 30000) return { text: 'Moderate', color: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-200' };
    if (rank < 50000) return { text: 'Accessible', color: 'text-green-700', bg: 'bg-green-100', border: 'border-green-200' };
    return { text: 'Easy to Get In', color: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-200' };
  };

  const loadDistrictData = useCallback(async (districtId: number, currentRankings?: DistrictRanking[]) => {
    const rankingsToUpdate = currentRankings || rankings;
    const districtIndex = rankingsToUpdate.findIndex(d => d.district_id === districtId);

    if (districtIndex === -1 || loadedDistricts.has(districtId)) return;

    const districtName = rankingsToUpdate[districtIndex].district_name;
    const cacheKey = `${selectedYear}-${selectedCommunity}-${districtName}`;

    if (districtDataCache.has(cacheKey)) {
      const cachedData = districtDataCache.get(cacheKey);
      const finalRankings = currentRankings ? [...currentRankings] : [...rankings];
      finalRankings[districtIndex] = {
        ...finalRankings[districtIndex],
        colleges: cachedData,
        isLoaded: true,
        isLoading: false,
      };
      setRankings(finalRankings);
      setLoadedDistricts(prev => new Set([...prev, districtId]));
      return;
    }

    const updatedRankings = [...rankingsToUpdate];
    updatedRankings[districtIndex].isLoading = true;
    if (!currentRankings) setRankings(updatedRankings);

    try {
      const response = await axios.get('/cutoffs/district-details', {
        params: { year: selectedYear, community: selectedCommunity, district: districtName },
      });

      const colleges = response.data.colleges || [];
      setDistrictDataCache(prev => new Map(prev).set(cacheKey, colleges));

      const finalRankings = currentRankings ? [...currentRankings] : [...rankings];
      finalRankings[districtIndex] = {
        ...finalRankings[districtIndex],
        colleges,
        isLoaded: true,
        isLoading: false,
      };

      setRankings(finalRankings);
      setLoadedDistricts(prev => new Set([...prev, districtId]));
    } catch (err: any) {
      console.error(`Error loading district ${districtId}:`, err);
      const finalRankings = currentRankings ? [...currentRankings] : [...rankings];
      finalRankings[districtIndex].isLoading = false;
      if (!currentRankings) setRankings(finalRankings);
    }
  }, [selectedYear, selectedCommunity, rankings, loadedDistricts, districtDataCache]);

  const loadCollegeDetails = useCallback(async (collegeId: number) => {
    const districtIndex = rankings.findIndex(d =>
      d.colleges.length > 0 && d.colleges[0].id === collegeId
    );

    if (districtIndex === -1 || loadedDistricts.has(collegeId)) return;

    const college = rankings[districtIndex].colleges[0];
    if (!college) return;

    const cacheKey = `college-${college.id}-${selectedYear}-${selectedCommunity}`;

    if (districtDataCache.has(cacheKey)) {
      const cachedDepartments = districtDataCache.get(cacheKey);
      const updatedRankings = [...rankings];
      updatedRankings[districtIndex].colleges[0].departments = cachedDepartments;
      updatedRankings[districtIndex].isLoaded = true;
      updatedRankings[districtIndex].isLoading = false;
      setRankings(updatedRankings);
      setLoadedDistricts(prev => new Set([...prev, collegeId]));
      return;
    }

    const updatedRankings = [...rankings];
    updatedRankings[districtIndex].isLoading = true;
    setRankings(updatedRankings);

    try {
      const response = await axios.get('/cutoffs/district-details', {
        params: {
          year: selectedYear,
          community: selectedCommunity,
          college_id: college.id
        },
      });

      const colleges = response.data.colleges || [];
      const departments = colleges.length > 0 ? colleges[0].departments : [];

      setDistrictDataCache(prev => new Map(prev).set(cacheKey, departments));

      const finalRankings = [...rankings];
      finalRankings[districtIndex].colleges[0].departments = departments;
      finalRankings[districtIndex].isLoaded = true;
      finalRankings[districtIndex].isLoading = false;

      setRankings(finalRankings);
      setLoadedDistricts(prev => new Set([...prev, collegeId]));
    } catch (err: any) {
      console.error(`Error loading college ${college.id}:`, err);
      const finalRankings = [...rankings];
      finalRankings[districtIndex].isLoading = false;
      setRankings(finalRankings);
    }
  }, [selectedYear, selectedCommunity, rankings, loadedDistricts, districtDataCache]);

  const toggleDistrict = useCallback(async (districtId: number, collegeId?: number) => {
    const newExpanded = new Set(expandedDistricts);
    const trackingId = rankingMethod === 'colleges' && collegeId ? collegeId : districtId;

    if (newExpanded.has(trackingId)) {
      newExpanded.delete(trackingId);
    } else {
      newExpanded.add(trackingId);
      if (rankingMethod === 'quality' && !loadedDistricts.has(districtId)) {
        await loadDistrictData(districtId);
      } else if (rankingMethod === 'colleges' && collegeId && !loadedDistricts.has(collegeId)) {
        await loadCollegeDetails(collegeId);
      }
    }

    setExpandedDistricts(newExpanded);
  }, [expandedDistricts, loadedDistricts, loadDistrictData, loadCollegeDetails, rankingMethod]);

  const toggleCollege = useCallback((collegeId: number) => {
    const newExpanded = new Set(expandedColleges);
    if (newExpanded.has(collegeId)) {
      newExpanded.delete(collegeId);
    } else {
      newExpanded.add(collegeId);
    }
    setExpandedColleges(newExpanded);
  }, [expandedColleges]);

  const toggleCollegeSelection = useCallback((college: College, event: React.MouseEvent) => {
    event.stopPropagation();
    const newSelected = new Map(selectedColleges);

    if (newSelected.has(college.id)) {
      newSelected.delete(college.id);
    } else {
      if (newSelected.size >= 5) {
        alert('You can select up to 5 colleges for comparison');
        return;
      }
      newSelected.set(college.id, {
        id: college.id,
        college_name: college.college_name,
        college_code: college.college_code,
        college_type: college.college_type,
      });
    }

    setSelectedColleges(newSelected);
  }, [selectedColleges]);

  const clearSelection = useCallback(() => {
    setSelectedColleges(new Map());
  }, []);

  const compareColleges = useCallback(() => {
    const collegeIds = Array.from(selectedColleges.keys());
    const collegesParam = collegeIds.join(',');
    navigate(`/cutoffs?colleges=${collegesParam}&community=${selectedCommunity}`);
  }, [selectedColleges, selectedCommunity, navigate]);

  const getDemandBadge = (trend: 'high_demand' | 'medium_demand' | 'low_demand') => {
    switch (trend) {
      case 'high_demand':
        return {
          icon: <FaArrowUp className="w-3 h-3" />,
          text: 'High Demand',
          className: 'bg-red-100 text-red-800 border-red-300',
        };
      case 'medium_demand':
        return {
          icon: <FaMinus className="w-3 h-3" />,
          text: 'Medium Demand',
          className: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        };
      case 'low_demand':
        return {
          icon: <FaArrowDown className="w-3 h-3" />,
          text: 'Low Demand',
          className: 'bg-green-100 text-green-800 border-green-300',
        };
    }
  };

  const getTrendIndicator = (rankChange: number) => {
    if (rankChange === 0) return { text: 'New', color: 'text-gray-600' };
    if (rankChange < -100) return { text: '↑ Harder', color: 'text-red-600' };
    if (rankChange > 100) return { text: '↓ Easier', color: 'text-green-600' };
    return { text: '→ Stable', color: 'text-gray-600' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading college rankings...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>District-wise Engineering College Rankings Tamil Nadu — TNEA</title>
        <meta name="description" content="Compare engineering colleges district-wise across Tamil Nadu. See cutoff trends, top departments, seat availability and college rankings by district for TNEA admissions." />
        <meta name="keywords" content="district wise engineering colleges Tamil Nadu, TNEA district rankings, best engineering colleges by district Tamil Nadu, Chennai engineering colleges, Coimbatore engineering colleges" />
        <meta property="og:title" content="District-wise Engineering College Rankings Tamil Nadu — TNEA" />
        <meta property="og:description" content="Compare engineering colleges district-wise across Tamil Nadu — cutoffs, departments, seats and rankings." />
        <meta property="og:url" content="https://choosemycollege.in/district-rankings" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://choosemycollege.in/district-rankings" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-8">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FaSearch className="w-8 h-8 text-primary-600" />
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {rankingMethod === 'colleges' ? 'Best Colleges' : 'Colleges Near You'}
                </h1>
              </div>
              {selectedColleges.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-semibold">
                    {selectedColleges.size}/5 selected
                  </span>
                </div>
              )}
            </div>
            <p className="text-gray-600 text-sm md:text-base">
              {rankingMethod === 'colleges'
                ? 'Top colleges ranked by last admitted rank. Lower rank = harder to get in. Click any college to see department-wise details.'
                : 'Find colleges in your district. Click any district to see all colleges and their departments.'}
            </p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="max-w-7xl mx-auto mb-4">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">View</h3>
                <p className="text-xs text-gray-500">
                  {rankingMethod === 'colleges'
                    ? 'All colleges ranked by competitiveness'
                    : 'Browse colleges grouped by your district'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setRankingMethod('colleges')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    rankingMethod === 'colleges'
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Best Colleges
                </button>
                <button
                  onClick={() => setRankingMethod('quality')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    rankingMethod === 'quality'
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Near My District
                </button>
                <button
                  onClick={() => setRankingMethod('bestvalue')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 ${
                    rankingMethod === 'bestvalue'
                      ? 'bg-amber-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaStar className="text-xs" /> Best Value
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
            <div className="flex items-center gap-2 mb-3">
              <FaFilter className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Community
                </label>
                <select
                  value={selectedCommunity}
                  onChange={(e) => setSelectedCommunity(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {communities.map((community) => (
                    <option key={community} value={community}>
                      {community}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  District
                </label>
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Districts</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  College Type
                </label>
                <select
                  value={selectedCollegeType}
                  onChange={(e) => setSelectedCollegeType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  {collegeTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Branch / Department
                </label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Branches</option>
                  {branches.filter((b) => !['Architecture', 'Design', 'Planning'].includes(b.category)).map((b) => (
                    <option key={b.branch_code} value={b.branch_code}>
                      {b.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Bar */}
        {!loading && !error && rankings.length > 0 && (
          <div className="max-w-7xl mx-auto mb-6">
            <div className="bg-white rounded-xl shadow-lg px-5 py-3 flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-gray-700 font-medium">
                {summaryText}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {activeFilters.map((filter, i) => (
                  <span key={i} className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs font-medium border border-primary-200">
                    {filter}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && rankings.length > 0 && (
          <div className="max-w-7xl mx-auto space-y-4">
            {rankings.map((district, districtIndex) => {
              const uniqueKey = rankingMethod === 'colleges' && district.colleges.length > 0
                ? `college-${district.colleges[0].id}`
                : `district-${district.district_id}`;

              const college = rankingMethod === 'colleges' && district.colleges.length > 0
                ? district.colleges[0]
                : null;

              const avgRank = college ? college.avg_rank_across_depts : 0;
              const competitiveness = college ? getCompetitivenessLabel(avgRank) : null;

              return (
                <div key={uniqueKey} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  {/* Card Header */}
                  <div
                    className={`p-4 md:p-5 cursor-pointer transition-all ${
                      rankingMethod === 'colleges'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                        : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800'
                    }`}
                    onClick={() => {
                      const collegeId = college ? college.id : undefined;
                      toggleDistrict(district.district_id, collegeId);
                    }}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-2xl font-bold text-white bg-white/20 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                          #{districtIndex + 1}
                        </span>
                        <div className="min-w-0">
                          <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                            {rankingMethod === 'colleges' ? (
                              <FaBuilding className="w-4 h-4 flex-shrink-0" />
                            ) : (
                              <FaMapMarkerAlt className="w-4 h-4 flex-shrink-0" />
                            )}
                            <span className="truncate">
                              {college ? college.college_name : district.district_name}
                            </span>
                          </h2>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {college ? (
                              <>
                                <span className="text-white/80 text-sm">{college.college_code}</span>
                                <span className="text-white/50">|</span>
                                <span className="text-white/80 text-sm">{district.district_name}</span>
                                <span className="text-white/50">|</span>
                                <span className="text-white/80 text-sm">{college.college_type}</span>
                                {competitiveness && (
                                  <>
                                    <span className="text-white/50">|</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${competitiveness.bg} ${competitiveness.color}`}>
                                      {competitiveness.text}
                                    </span>
                                  </>
                                )}
                              </>
                            ) : (
                              <span className="text-white/80 text-sm">
                                {district.total_colleges} colleges &middot; {district.total_departments} departments
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-white flex items-center gap-3 flex-shrink-0">
                        {college && (
                          <div className="text-right hidden sm:block">
                            <p className="text-white/70 text-xs whitespace-nowrap">Last Admitted Rank</p>
                            <p className="text-xl font-bold">{avgRank.toLocaleString()}</p>
                          </div>
                        )}
                        {district.isLoading && <FaSpinner className="animate-spin w-5 h-5" />}
                        {(() => {
                          const trackingId = college ? college.id : district.district_id;
                          return expandedDistricts.has(trackingId) ? '▼' : '▶';
                        })()}
                        {/* Compare button for colleges mode */}
                        {college && (
                          <button
                            onClick={(e) => toggleCollegeSelection(college, e)}
                            className={`p-2 rounded-full transition-all flex-shrink-0 ${
                              selectedColleges.has(college.id)
                                ? 'bg-white text-blue-600 hover:bg-gray-100'
                                : 'bg-white/20 text-white hover:bg-white/30 border-2 border-white'
                            }`}
                            title={selectedColleges.has(college.id) ? 'Remove from comparison' : 'Add to comparison'}
                          >
                            {selectedColleges.has(college.id) ? (
                              <FaCheck className="w-4 h-4" />
                            ) : (
                              <FaPlus className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {(() => {
                    const trackingId = college ? college.id : district.district_id;
                    return expandedDistricts.has(trackingId);
                  })() && (
                    <div className="p-4 md:p-6">
                      {/* Colleges mode - show departments */}
                      {rankingMethod === 'colleges' && college && (
                        <>
                          {district.isLoading && (
                            <div className="space-y-3 animate-pulse">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                  <div className="flex-1 space-y-2">
                                    <div className="h-5 bg-gray-200 rounded w-2/3"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                                  </div>
                                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                                </div>
                              ))}
                            </div>
                          )}

                          {district.isLoaded && college.departments && college.departments.length > 0 && (
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <FaGraduationCap className="w-5 h-5 text-primary-600" />
                                Departments (High to Low Demand)
                              </h3>
                              <div className="space-y-3">
                                {college.departments.map((dept: DepartmentDemand, deptIndex: number) => {
                                  const deptCompetitiveness = getCompetitivenessLabel(dept.avg_closing_rank);
                                  return (
                                    <div
                                      key={`${dept.branch_code}-${deptIndex}`}
                                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:shadow-md transition-shadow bg-white"
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-sm font-bold text-gray-600">
                                            {deptIndex + 1}.
                                          </span>
                                          <h4 className="text-sm md:text-base font-semibold text-gray-900">
                                            {dept.branch_name}
                                          </h4>
                                          <span className="text-xs text-gray-500">({dept.branch_code})</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 text-xs mt-2">
                                          <span className={`px-2 py-1 rounded-full font-medium ${deptCompetitiveness.bg} ${deptCompetitiveness.color}`}>
                                            {deptCompetitiveness.text}
                                          </span>
                                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                            Seats: {dept.seats_filled}/{dept.total_seats} ({dept.fill_rate}%)
                                          </span>
                                          {dept.rank_change_5yr !== 0 && (
                                            <span className={`px-2 py-1 rounded-full flex items-center gap-1 ${
                                              dept.rank_change_5yr > 0
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-green-100 text-green-800'
                                            }`}>
                                              {dept.rank_change_5yr > 0 ? (
                                                <><FaArrowUp className="w-3 h-3" /> Harder</>
                                              ) : (
                                                <><FaArrowDown className="w-3 h-3" /> Easier</>
                                              )}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right ml-4">
                                        <p className="text-xs text-gray-500">Last Admitted Rank</p>
                                        <p className="text-lg md:text-xl font-bold text-primary-600">
                                          {dept.avg_closing_rank?.toLocaleString()}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {district.isLoaded && (!college.departments || college.departments.length === 0) && (
                            <div className="text-center py-12">
                              <p className="text-gray-600">No department data available</p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Quality mode - show colleges in district */}
                      {rankingMethod === 'quality' && (
                        <>
                          {district.isLoading && (
                            <div className="space-y-4 animate-pulse">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="border border-gray-200 rounded-lg p-4">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 space-y-3">
                                      <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                                      <div className="flex gap-2">
                                        <div className="h-5 bg-gray-200 rounded w-20"></div>
                                        <div className="h-5 bg-gray-200 rounded w-24"></div>
                                        <div className="h-5 bg-gray-200 rounded w-16"></div>
                                      </div>
                                    </div>
                                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {district.isLoaded && district.colleges.length === 0 && (
                            <div className="text-center py-12">
                              <p className="text-gray-600">No colleges found in this district</p>
                            </div>
                          )}

                          {district.isLoaded && district.colleges.length > 0 && (
                            <div className="space-y-4">
                              {district.colleges.map((collegeItem, collegeIndex) => {
                                const collegeCompetitiveness = getCompetitivenessLabel(collegeItem.avg_rank_across_depts);
                                return (
                                  <div
                                    key={collegeItem.id}
                                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                                  >
                                    <div
                                      className={`bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors ${
                                        selectedColleges.has(collegeItem.id) ? 'ring-2 ring-primary-500' : ''
                                      }`}
                                      onClick={() => toggleCollege(collegeItem.id)}
                                    >
                                      <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-2">
                                            <span className="text-lg font-bold text-primary-600">
                                              #{collegeIndex + 1}
                                            </span>
                                            <FaBuilding className="w-5 h-5 text-gray-600 flex-shrink-0" />
                                            <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">
                                              {collegeItem.college_name}
                                            </h3>
                                          </div>
                                          <div className="flex flex-wrap gap-2 text-xs">
                                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                                              {collegeItem.college_code}
                                            </span>
                                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium">
                                              {collegeItem.college_type}
                                            </span>
                                            {collegeItem.autonomous_status && (
                                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                                                Autonomous
                                              </span>
                                            )}
                                            {collegeItem.naac_grade && (
                                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                                                NAAC: {collegeItem.naac_grade}
                                              </span>
                                            )}
                                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full font-medium">
                                              {collegeItem.total_departments} departments
                                            </span>
                                            <span className={`px-2 py-1 rounded-full font-medium ${collegeCompetitiveness.bg} ${collegeCompetitiveness.color}`}>
                                              {collegeCompetitiveness.text}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                          <div className="text-right">
                                            <p className="text-xs text-gray-500 whitespace-nowrap">Last Admitted Rank</p>
                                            <p className="text-xl md:text-2xl font-bold text-primary-600">
                                              {collegeItem.avg_rank_across_depts.toLocaleString()}
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => toggleCollegeSelection(collegeItem, e)}
                                            className={`p-2 rounded-full transition-all flex-shrink-0 ${
                                              selectedColleges.has(collegeItem.id)
                                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                                : 'bg-white border-2 border-gray-300 text-gray-600 hover:border-primary-500 hover:text-primary-600'
                                            }`}
                                            title={selectedColleges.has(collegeItem.id) ? 'Remove from comparison' : 'Add to comparison'}
                                          >
                                            {selectedColleges.has(collegeItem.id) ? (
                                              <FaCheck className="w-4 h-4" />
                                            ) : (
                                              <FaPlus className="w-4 h-4" />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    </div>

                                    {expandedColleges.has(collegeItem.id) && (
                                      <div className="p-4 bg-white">
                                        <div className="flex items-center gap-2 mb-3">
                                          <FaGraduationCap className="w-5 h-5 text-gray-600" />
                                          <h4 className="font-semibold text-gray-900">
                                            Departments (High to Low Demand)
                                          </h4>
                                        </div>
                                        <div className="space-y-2">
                                          {collegeItem.departments.map((dept, deptIndex) => {
                                            const badge = getDemandBadge(dept.trend);
                                            const trendIndicator = getTrendIndicator(dept.rank_change_5yr);
                                            const deptComp = getCompetitivenessLabel(dept.avg_closing_rank);

                                            return (
                                              <div
                                                key={`${dept.branch_code}-${deptIndex}`}
                                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                              >
                                                <div className="flex-1">
                                                  <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-semibold text-gray-700">
                                                      {deptIndex + 1}.
                                                    </span>
                                                    <p className="text-sm font-semibold text-gray-900">
                                                      {dept.branch_name}
                                                    </p>
                                                    <span className="text-xs text-gray-500">
                                                      ({dept.branch_code})
                                                    </span>
                                                  </div>
                                                  <div className="flex flex-wrap gap-2 items-center">
                                                    <span
                                                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border ${badge.className}`}
                                                    >
                                                      {badge.icon}
                                                      {badge.text}
                                                    </span>
                                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${deptComp.bg} ${deptComp.color}`}>
                                                      {deptComp.text}
                                                    </span>
                                                    {dept.rank_change_5yr !== 0 && (
                                                      <span className={`text-xs font-medium ${trendIndicator.color}`}>
                                                        {trendIndicator.text}
                                                      </span>
                                                    )}
                                                    {dept.total_seats > 0 && (
                                                      <span className="text-xs text-gray-600">
                                                        Seats: {dept.seats_filled}/{dept.total_seats} ({dept.fill_rate.toFixed(0)}%)
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="text-right ml-4">
                                                  <p className="text-xs text-gray-500">Last Admitted Rank</p>
                                                  <p className="text-lg font-bold text-gray-900">
                                                    {dept.avg_closing_rank.toLocaleString()}
                                                  </p>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && rankings.length === 0 && rankingMethod !== 'bestvalue' && (
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <FaMapMarkerAlt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                No results found for the selected filters. Try changing the community, year, or other filters.
              </p>
            </div>
          </div>
        )}

        {/* Best Value View */}
        {rankingMethod === 'bestvalue' && (
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <FaStar className="text-amber-500 text-lg" />
                <div>
                  <h2 className="font-bold text-gray-800">Best Value Colleges</h2>
                  <p className="text-xs text-gray-500">Score = NAAC grade (40%) + Fees (40%) + College type (20%). Government and top-rated colleges rank highest.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <select
                  value={bestValueDistrict}
                  onChange={(e) => setBestValueDistrict(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                >
                  <option value="">All Districts</option>
                  {districts.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>

            {bestValueLoading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mb-4" />
                <p className="text-gray-500 text-sm">Computing best value scores…</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50 border-b border-amber-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-10">#</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">College</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">District</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">NAAC</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Fee/yr</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {bestValueColleges.map((col: any, idx: number) => {
                      const campusMatch = col.college_name.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
                      const name = campusMatch
                        ? `${campusMatch[1].split(',')[0].trim()} — ${campusMatch[2]}`
                        : col.college_name.length > 55 && col.college_name.includes(',')
                          ? col.college_name.substring(0, col.college_name.indexOf(','))
                          : col.college_name;
                      return (
                        <tr key={col.id} className="hover:bg-amber-50/30 transition-colors">
                          <td className="px-4 py-3 text-gray-400 font-medium">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <Link to={`/colleges/${col.college_code}`} className="font-semibold text-gray-800 hover:text-blue-700 hover:underline leading-snug block">
                              {name}
                            </Link>
                            <span className={`inline-block mt-0.5 px-2 py-0.5 rounded text-[10px] font-medium ${
                              col.college_type === 'Government' ? 'bg-blue-50 text-blue-700' :
                              col.college_type === 'Anna University' || col.college_type === 'Anna Univ - Constituent' ? 'bg-purple-50 text-purple-700' :
                              col.college_type === 'Aided' || col.college_type === 'Govt Aided' ? 'bg-teal-50 text-teal-700' :
                              'bg-gray-50 text-gray-600'
                            }`}>
                              {col.college_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{col.district?.name}</td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            {col.naac_grade
                              ? <span className="font-semibold text-purple-700">{col.naac_grade}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                            {col.annual_tuition_fee ? `₹${Number(col.annual_tuition_fee).toLocaleString('en-IN')}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-bold text-amber-700">{col.bestValueScore.toFixed(0)}</span>
                            <span className="text-gray-300 text-xs">/100</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Floating Compare Button */}
      {selectedColleges.size >= 2 && (
        <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 animate-bounce">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-primary-500 p-4 max-w-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-700">
                  {selectedColleges.size} colleges selected
                </p>
                <p className="text-xs text-gray-500">
                  {selectedColleges.size < 5 && `Add ${5 - selectedColleges.size} more (max 5)`}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={clearSelection}
                  className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                  title="Clear selection"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
                <button
                  onClick={compareColleges}
                  className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-bold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <span>Compare Now</span>
                  <FaArrowUp className="w-4 h-4 rotate-90" />
                </button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {Array.from(selectedColleges.values()).map((col) => (
                <span
                  key={col.id}
                  className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-medium"
                >
                  {col.college_code}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DistrictRankings;
